/**
 * DiagramsList — Iter Feb 2026 R39.
 *
 * The library page for the Flowchart / Workflow Diagram module. Users
 * see diagrams they own (private) + workspace-wide + scoped-to-them
 * (client/brand/project). Workspace owners see ALL as designed.
 *
 * Innovation surface exposed here:
 *   • "Ask AI" primary CTA — generates a flowchart from plain English
 *     (Claude Sonnet 4.5) and drops the user into the editor
 *   • Templates gallery drawer (system + workspace templates)
 *   • Scope tabs to slice the collection
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Sparkles, LayoutTemplate, Workflow, Globe2, Building2, Package as PackageIcon,
  Lock, Users as UsersIcon, Search, ArrowRight, Wand2, Copy, Trash2, Crown, Layers as LayersIcon,
} from "lucide-react";

const SCOPE_TABS = [
  { key: "all",       label: "All",       icon: LayoutTemplate },
  { key: "private",   label: "Private",   icon: Lock },
  { key: "workspace", label: "Workspace", icon: Globe2 },
  { key: "client",    label: "Client",    icon: UsersIcon },
  { key: "brand",     label: "Brand",     icon: PackageIcon },
  { key: "project",   label: "Project",   icon: Building2 },
];

const SCOPE_META = {
  private:   { color: "border-neutral-400 text-neutral-600 bg-neutral-50",   icon: Lock },
  workspace: { color: "border-blue-400 text-blue-700 bg-blue-50",             icon: Globe2 },
  client:    { color: "border-emerald-400 text-emerald-700 bg-emerald-50",    icon: UsersIcon },
  brand:     { color: "border-purple-400 text-purple-700 bg-purple-50",       icon: PackageIcon },
  project:   { color: "border-amber-400 text-amber-700 bg-amber-50",          icon: Building2 },
};

export default function DiagramsList() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState("all");
  const [q, setQ] = useState("");
  const [ownerView, setOwnerView] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [layout, setLayout] = useState(() => localStorage.getItem("acq_diagrams_layout") || "grid");
  useEffect(() => { localStorage.setItem("acq_diagrams_layout", layout); }, [layout]);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (scope !== "all") p.set("scope", scope);
      if (q.trim()) p.set("q", q.trim());
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/diagrams?${p}`);
      setRows(data?.items || []);
      setOwnerView(Boolean(data?.is_owner_view));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load diagrams"));
    } finally { setLoading(false); }
  }, [activeWorkspaceId, scope, q]);

  useEffect(() => { load(); }, [load]);

  const empty = !loading && rows.length === 0;

  return (
    <div className="flex flex-col h-full" data-testid="diagrams-page">
      <PageHeader
        title="Diagrams"
        description="Design flowcharts and process diagrams — private, or shared with your workspace, a client, brand, or project. Owners see everything."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" className="rounded-none h-9" onClick={() => setTplOpen(true)} data-testid="diagrams-templates-btn">
              <LayoutTemplate className="w-4 h-4 mr-1.5" /> Templates
            </Button>
            <Button variant="outline" className="rounded-none h-9" onClick={() => setAiOpen(true)} data-testid="diagrams-ai-btn">
              <Wand2 className="w-4 h-4 mr-1.5" /> Ask AI
            </Button>
            <Button className="rounded-none h-9" onClick={() => setCreateOpen(true)} data-testid="diagrams-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New diagram
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-4 space-y-4 flex-1 overflow-auto">
        {ownerView && (
          <div className="inline-flex items-center gap-2 border border-amber-300 bg-amber-50 text-amber-800 px-3 py-1 text-xs">
            <Crown className="w-3.5 h-3.5" /> Owner view — you can see every diagram in this workspace.
          </div>
        )}

        <div className="flex items-center gap-1 flex-wrap border-b border-border">
          {SCOPE_TABS.map(t => {
            const Icon = t.icon;
            const active = scope === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setScope(t.key)}
                data-testid={`diagrams-tab-${t.key}`}
                className={`text-sm px-3 py-2 border-b-2 flex items-center gap-1.5 transition-colors ${active ? "border-foreground text-foreground font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
          <div className="flex-1" />
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title…" className="h-8 pl-8 w-56 rounded-none text-xs" data-testid="diagrams-search" />
          </div>
          <div className="border ml-1 flex">
            <button onClick={() => setLayout("grid")} className={`px-2 py-1 text-xs ${layout === "grid" ? "bg-neutral-900 text-white" : "hover:bg-neutral-100"}`} data-testid="diagrams-view-grid" title="Card view">▦</button>
            <button onClick={() => setLayout("list")} className={`px-2 py-1 text-xs ${layout === "list" ? "bg-neutral-900 text-white" : "hover:bg-neutral-100"}`} data-testid="diagrams-view-list" title="List view">≡</button>
          </div>
        </div>

        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

        {empty && (
          <div className="border-2 border-dashed p-10 text-center space-y-2" data-testid="diagrams-empty">
            <Workflow className="w-10 h-10 text-neutral-400 mx-auto" />
            <div className="font-medium">No diagrams here yet</div>
            <div className="text-sm text-muted-foreground">Start from a template, generate one with AI, or draw from scratch.</div>
            <div className="flex items-center gap-2 justify-center pt-2">
              <Button variant="outline" className="rounded-none" onClick={() => setTplOpen(true)}>
                <LayoutTemplate className="w-4 h-4 mr-1.5" /> Templates
              </Button>
              <Button variant="outline" className="rounded-none" onClick={() => setAiOpen(true)}>
                <Wand2 className="w-4 h-4 mr-1.5" /> Ask AI
              </Button>
              <Button className="rounded-none" onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-1.5" /> Blank canvas
              </Button>
            </div>
          </div>
        )}

        {!empty && layout === "grid" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map(r => (
              <DiagramCard key={r.id} r={r} onOpen={() => navigate(`/app/diagrams/${r.id}`)} onReload={load} />
            ))}
          </div>
        )}
        {!empty && layout === "list" && (
          <div className="border">
            <table className="w-full text-xs" data-testid="diagrams-list-table">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr className="border-b">
                  <th className="text-left px-2 py-1.5">Title</th>
                  <th className="text-left px-2 py-1.5">Scope</th>
                  <th className="text-right px-2 py-1.5">Nodes</th>
                  <th className="text-right px-2 py-1.5">Edges</th>
                  <th className="text-right px-2 py-1.5">Version</th>
                  <th className="text-left px-2 py-1.5">Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b hover:bg-neutral-50 cursor-pointer" onClick={() => navigate(`/app/diagrams/${r.id}`)} data-testid={`diagrams-list-row-${r.id}`}>
                    <td className="px-2 py-1.5 font-semibold truncate max-w-[320px]" title={r.title}>{r.title}</td>
                    <td className="px-2 py-1.5">{r.scope}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.node_count}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.edge_count}</td>
                    <td className="px-2 py-1.5 text-right font-mono">v{r.version || 1}</td>
                    <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{r.updated_at?.slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {createOpen && (
        <CreateDialog
          wsId={activeWorkspaceId}
          onClose={() => setCreateOpen(false)}
          onDone={(row) => { setCreateOpen(false); if (row?.id) navigate(`/app/diagrams/${row.id}`); }}
        />
      )}
      {aiOpen && (
        <AiDialog
          wsId={activeWorkspaceId}
          onClose={() => setAiOpen(false)}
          onDone={(row) => { setAiOpen(false); if (row?.id) navigate(`/app/diagrams/${row.id}`); }}
        />
      )}
      {tplOpen && (
        <TemplatesDialog
          wsId={activeWorkspaceId}
          onClose={() => setTplOpen(false)}
          onPick={(tpl) => { setTplOpen(false); startFromTemplate(activeWorkspaceId, tpl, navigate); }}
        />
      )}
    </div>
  );
}

async function startFromTemplate(wsId, tpl, navigate) {
  try {
    const { data } = await api.post(`/workspaces/${wsId}/diagrams`, {
      title: `${tpl.title} — copy`,
      description: tpl.description,
      scope: "private",
      nodes: tpl.nodes || [],
      edges: tpl.edges || [],
      template_source: tpl.id,
    });
    if (data?.id) navigate(`/app/diagrams/${data.id}`);
  } catch (e) { toast.error(apiErrorMessage(e, "Failed to create from template")); }
}

function DiagramCard({ r, onOpen, onReload }) {
  const meta = SCOPE_META[r.scope] || SCOPE_META.private;
  const Icon = meta.icon;
  const [busy, setBusy] = useState(false);
  const dup = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post(`/diagrams/${r.id}/duplicate`);
      toast.success("Duplicated to your private diagrams");
      onReload?.();
      return data;
    } catch (err) { toast.error(apiErrorMessage(err, "Duplicate failed")); }
    finally { setBusy(false); }
  };
  const del = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/diagrams/${r.id}`);
      toast.success("Deleted");
      onReload?.();
    } catch (err) { toast.error(apiErrorMessage(err, "Delete failed")); }
    finally { setBusy(false); }
  };
  return (
    <button
      onClick={onOpen}
      className="border p-3 text-left hover:border-foreground transition-colors flex flex-col gap-2 group"
      data-testid={`diagram-card-${r.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
            <Icon className="w-3 h-3" /> {r.scope}
            {r.public_share_enabled && <span className="ml-1 px-1 border border-emerald-300 text-emerald-700 rounded-sm">shared</span>}
          </div>
          <div className="font-semibold truncate mt-0.5">{r.title}</div>
          {r.description && <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{r.description}</div>}
        </div>
        <span className={`text-[10px] px-2 py-0.5 border ${meta.color}`}>{r.scope}</span>
      </div>

      <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
        <span className="flex items-center gap-1"><LayersIcon className="w-3 h-3" /> {r.node_count} nodes</span>
        <span>·</span>
        <span>{r.edge_count} edges</span>
        <span>·</span>
        <span>v{r.version || 1}</span>
        <div className="flex-1" />
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          <span onClick={dup} title="Duplicate" role="button" aria-disabled={busy} className="hover:text-foreground p-1" data-testid={`diagram-dup-${r.id}`}>
            <Copy className="w-3.5 h-3.5" />
          </span>
          <span onClick={del} title="Delete" role="button" aria-disabled={busy} className="hover:text-rose-600 p-1" data-testid={`diagram-del-${r.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </span>
        </div>
        <ArrowRight className="w-3.5 h-3.5 opacity-60" />
      </div>

      {(r.tags || []).slice(0, 4).map(t => (
        <span key={t} className="text-[10px] border px-1 mr-1 inline-block">{t}</span>
      ))}
    </button>
  );
}

// ─────────────── Create dialog ───────────────

function CreateDialog({ wsId, onClose, onDone }) {
  const [form, setForm] = useState({ title: "", description: "", scope: "private", client_id: "", brand_id: "", project_id: "" });
  const [busy, setBusy] = useState(false);
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/clients`).then(r => setClients(r.data?.rows || r.data || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/brands`).then(r => setBrands(r.data?.rows || r.data || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/projects`).then(r => setProjects(r.data?.rows || r.data || [])).catch(() => {});
  }, [wsId]);
  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (form.scope === "client" && !form.client_id) return toast.error("Pick a client for client-scoped diagram");
    if (form.scope === "brand" && !form.brand_id) return toast.error("Pick a brand for brand-scoped diagram");
    if (form.scope === "project" && !form.project_id) return toast.error("Pick a project for project-scoped diagram");
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/diagrams`, {
        title: form.title.trim(),
        description: form.description.trim() || null,
        scope: form.scope,
        client_id: form.client_id || null,
        brand_id: form.brand_id || null,
        project_id: form.project_id || null,
        nodes: [], edges: [],
      });
      toast.success("Diagram created");
      onDone?.(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Workflow className="w-4 h-4" /> New diagram</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="h-9 rounded-none" data-testid="diagram-form-title" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-none text-sm" />
          </div>
          <div>
            <Label className="text-xs">Scope</Label>
            <Select value={form.scope} onValueChange={v => setForm({ ...form, scope: v })}>
              <SelectTrigger className="h-9 rounded-none" data-testid="diagram-form-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Private (just me)</SelectItem>
                <SelectItem value="workspace">Workspace (all members)</SelectItem>
                <SelectItem value="client">Client-scoped</SelectItem>
                <SelectItem value="brand">Brand-scoped</SelectItem>
                <SelectItem value="project">Project-scoped</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">You can attach client / brand / project inside the editor once created.</p>
          </div>
          {form.scope === "client" && (
            <div>
              <Label className="text-xs">Client *</Label>
              <Select value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="h-9 rounded-none" data-testid="diagram-form-client"><SelectValue placeholder="Pick client…" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {form.scope === "brand" && (
            <div>
              <Label className="text-xs">Brand *</Label>
              <Select value={form.brand_id} onValueChange={v => setForm({ ...form, brand_id: v })}>
                <SelectTrigger className="h-9 rounded-none" data-testid="diagram-form-brand"><SelectValue placeholder="Pick brand…" /></SelectTrigger>
                <SelectContent>{brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {form.scope === "project" && (
            <div>
              <Label className="text-xs">Project *</Label>
              <Select value={form.project_id} onValueChange={v => setForm({ ...form, project_id: v })}>
                <SelectTrigger className="h-9 rounded-none" data-testid="diagram-form-project"><SelectValue placeholder="Pick project…" /></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="diagram-form-submit">
            {busy ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── AI dialog ───────────────

function AiDialog({ wsId, onClose, onDone }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (prompt.trim().length < 6) return toast.error("Describe your process in at least a sentence.");
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/diagrams/ai-generate`, {
        prompt: prompt.trim(),
        scope: "private",
      });
      toast.success("Diagram generated");
      onDone?.(data);
    } catch (e) { toast.error(apiErrorMessage(e, "AI generation failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Ask AI to draft a diagram</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Describe your process in plain English</Label>
            <Textarea
              rows={6}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="e.g. Client onboarding: sales sends contract, client signs, finance raises invoice, PM kicks off project, weekly status calls until go-live."
              className="rounded-none text-sm"
              data-testid="diagram-ai-prompt"
            />
          </div>
          <div className="text-[11px] text-muted-foreground border-l-2 border-amber-400 pl-2">
            Powered by Claude Sonnet 4.5 · You can edit every node after generation. Draft goes into your private diagrams — change scope later inside the editor.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="diagram-ai-submit">
            {busy ? "Drafting…" : (<><Wand2 className="w-4 h-4 mr-1.5" /> Generate</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Templates dialog ───────────────

function TemplatesDialog({ wsId, onClose, onPick }) {
  const [data, setData] = useState({ system: [], workspace: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/diagrams/templates`)
      .then(r => setData(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Failed to load templates")))
      .finally(() => setLoading(false));
  }, [wsId]);
  const all = useMemo(() => ([...data.system, ...data.workspace]), [data]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LayoutTemplate className="w-4 h-4" /> Templates</DialogTitle>
        </DialogHeader>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!loading && all.length === 0 && <div className="text-sm text-muted-foreground">No templates yet.</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {all.map(t => (
            <button
              key={t.id}
              onClick={() => onPick(t)}
              className="border p-3 text-left hover:border-foreground transition-colors"
              data-testid={`diagram-tpl-${t.id}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t.category || "Custom"}</div>
              <div className="font-semibold mt-0.5">{t.title}</div>
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</div>
              <div className="text-[11px] text-muted-foreground mt-2">{(t.nodes || []).length} nodes · {(t.edges || []).length} edges</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
